import { Action } from '../actions/ActionInterface';
import { ActionTypes } from '../actions/ActionTypes';

export interface FiltersState {
  searchTerm: string;
  typeFilter: string;
  breweryFilter: string;
  drinkTypeFilter: string;
  availabilityFilter: string;
  tagFilters: string[];
  showOnlyFavorites: boolean;
  showOnlyAvailable: boolean;
  filterByFavorites: boolean;
}

const initialState: FiltersState = {
  searchTerm: '',
  typeFilter: '',
  breweryFilter: '',
  drinkTypeFilter: '',
  availabilityFilter: '',
  tagFilters: [],
  showOnlyFavorites: false,
  showOnlyAvailable: false,
  filterByFavorites: false,
};

/**
 * Filters reducer
 * Handles all actions related to filtering beers
 * Following Single Responsibility Principle by focusing only on filters
 */
export function filtersReducer(state: FiltersState = initialState, action: Action): FiltersState {
  // Use the action's execute method if available (Command pattern)
  // Check if execute exists without causing TypeScript errors
  if (action && typeof (action as any).execute === 'function') {
    // The execute method expects the full state, so we need to adapt it
    const fullState = { filters: state } as any;
    const newState = action.execute(fullState);
    
    // Extract only the filters property this reducer is responsible for
    return newState.filters;
  }
  
  // Fallback to traditional switch statement for backward compatibility
  switch (action.type) {
    case ActionTypes.SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload || '',
      };
      
    case ActionTypes.SET_TYPE_FILTER:
      return {
        ...state,
        typeFilter: action.payload || '',
      };
      
    case ActionTypes.SET_BREWERY_FILTER:
      return {
        ...state,
        breweryFilter: action.payload || '',
      };
      
    case ActionTypes.SET_DRINK_TYPE_FILTER:
      return {
        ...state,
        drinkTypeFilter: action.payload || '',
      };
      
    case ActionTypes.SET_AVAILABILITY_FILTER:
      return {
        ...state,
        availabilityFilter: action.payload || '',
      };
      
    case ActionTypes.ADD_TAG_FILTER: {
      const tag = action.payload;
      
      if (!tag || state.tagFilters.includes(tag)) {
        return state;
      }
      
      return {
        ...state,
        tagFilters: [...state.tagFilters, tag],
      };
    }
      
    case ActionTypes.REMOVE_TAG_FILTER: {
      const tag = action.payload;
      
      if (!tag) {
        return state;
      }
      
      return {
        ...state,
        tagFilters: state.tagFilters.filter(t => t !== tag),
      };
    }
      
    case ActionTypes.CLEAR_TAG_FILTERS:
      return {
        ...state,
        tagFilters: [],
      };
      
    case ActionTypes.CLEAR_ALL_FILTERS:
      return {
        ...state,
        searchTerm: '',
        typeFilter: '',
        breweryFilter: '',
        drinkTypeFilter: '',
        availabilityFilter: '',
        tagFilters: [],
      };
      
    case ActionTypes.SET_SHOW_ONLY_FAVORITES:
      return {
        ...state,
        showOnlyFavorites: action.payload || false,
      };
      
    case ActionTypes.SET_SHOW_ONLY_AVAILABLE:
      return {
        ...state,
        showOnlyAvailable: action.payload || false,
      };
      
    case ActionTypes.SET_FILTER_BY_FAVORITES:
      return {
        ...state,
        filterByFavorites: action.payload || false,
      };
      
    default:
      return state;
  }
}
